import { useCallback, useEffect, useState } from 'react';
import { StatusBadge } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { formatIsraeli, type PhoneLookup } from '@/components/telephony/PhoneCell';
import { adminApi, type CdrSentiment } from '@/shared/api/adminApi';
import { useAudioPlayer } from '@/shared/stores/audioPlayer';
import { toast } from '@/components/ui/Toast';
import './cdr.css';

/** שורת CDR כפי שחוזרת מ-GET /admin/cdr. */
export interface CdrRow {
  id: number;
  direction: string;
  status: string;
  from_number: string;
  to_number: string;
  from_lookup?: unknown;
  to_lookup?: unknown;
  started_at: string;
  answered_at?: string | null;
  ended_at?: string | null;
  duration_sec: number;
  talk_sec: number;
  disposition?: string | null;
  hangup_cause?: string | null;
  recording_url?: string | null;
  agent_id?: number | null;
  agent_name?: string | null;
  campaign_id?: number | null;
  queue_id?: number | null;
  ai_summary?: string | null;
  ai_sentiment?: string | null;
}

export function directionLabel(d: string): string {
  return d === 'inbound' ? 'נכנסת' : d === 'outbound' ? 'יוצאת' : 'פנימית';
}

/** הצד השני של השיחה: המתקשר בנכנסת, היעד ביוצאת/פנימית. */
export function remoteParty(r: CdrRow): { number: string; lookup: PhoneLookup | null } {
  return r.direction === 'inbound'
    ? { number: r.from_number, lookup: (r.from_lookup as PhoneLookup | null) ?? null }
    : { number: r.to_number, lookup: (r.to_lookup as PhoneLookup | null) ?? null };
}

/** "היום 17:20" / "אתמול 09:40" / "22.9 17:40". */
export function whenLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hm = d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days === 0) return `היום ${hm}`;
  if (days === 1) return `אתמול ${hm}`;
  return `${d.getDate()}.${d.getMonth() + 1} ${hm}`;
}

// שני האיותים (no-answer/no_answer) קיימים בנתונים היסטוריים — מכסים את שניהם.
export function statusTone(s: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (s === 'completed' || s === 'answered') return 'ok';
  if (s === 'missed' || s === 'no-answer' || s === 'no_answer' || s === 'abandoned' || s === 'busy') return 'warn';
  if (s === 'failed' || s === 'dead') return 'danger';
  return 'neutral';
}
export function statusLabel(s: string): string {
  return ({
    completed: 'הסתיים', answered: 'נענה', ringing: 'מצלצלת', missed: 'הוחמץ',
    'no-answer': 'אין מענה', no_answer: 'אין מענה', abandoned: 'ננטש',
    busy: 'תפוס', failed: 'נכשל', dead: 'נותק',
  } as Record<string, string>)[s] ?? s;
}

export function fmt(s?: number): string {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}

/** כיוון: אייקון לא-כיווני (חצים מתהפכים לא נכון ב-RTL) + טקסט צבוע. */
export function DirectionCell({ direction }: { direction: string }) {
  const color = direction === 'inbound' ? 'var(--ok)' : direction === 'outbound' ? 'var(--accent-ink)' : 'var(--muted)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color, fontSize: 'var(--t-sm)' }}>
      <Icon name={direction === 'inbound' ? 'phoneIn' : direction === 'outbound' ? 'phoneOut' : 'phone'} size={14} />
      {directionLabel(direction)}
    </span>
  );
}

/** איש קשר: שם מודגש (אם זוהה) ומספר מתחתיו; תגי מאומת/ספאם נשמרים. */
export function ContactCell({ number, lookup }: { number: string; lookup: PhoneLookup | null }) {
  const phone = formatIsraeli(number);
  const badges = (
    <>
      {lookup?.is_verified && <span title="מאומת"><Icon name="ok" size={13} color="var(--ok)" /></span>}
      {lookup?.is_spam && <StatusBadge tone="danger" size="sm">{lookup.label ?? 'ספאם'}</StatusBadge>}
    </>
  );
  if (!lookup?.name) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span dir="ltr" style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{phone}</span>
        {badges}
      </span>
    );
  }
  return (
    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>{lookup.name}{badges}</span>
      <span dir="ltr" style={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', textAlign: 'end' }}>{phone}</span>
    </span>
  );
}

export function SentimentBadge({ value }: { value: CdrSentiment | string }) {
  const map = {
    positive: { label: 'חיובי', tone: 'ok' },
    neutral: { label: 'ניטרלי', tone: 'neutral' },
    negative: { label: 'שלילי', tone: 'danger' },
  } as const;
  const m = map[value as CdrSentiment] ?? map.neutral;
  return <StatusBadge tone={m.tone} size="sm">{m.label}</StatusBadge>;
}

export function Row2({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
      <span style={{ color: 'var(--muted)' }}>{k}</span>
      <span dir={mono ? 'ltr' : undefined} style={{ fontFamily: mono ? 'var(--font-mono)' : undefined }}>{v}</span>
    </div>
  );
}

/** כפתור השמעה עגול (32px בטבלה, 46px בכרטיס הקלטה). */
export function PlayButton({ playing, busy, failed, onClick, size = 32, strong }: {
  playing: boolean; busy?: boolean; failed?: boolean; onClick: () => void; size?: number; strong?: boolean;
}) {
  const bg = playing ? 'var(--accent)' : strong ? 'var(--text)' : 'var(--surface-3)';
  const fg = playing ? 'var(--on-accent)' : strong ? 'var(--bg)' : 'var(--text)';
  return (
    <button
      type="button"
      aria-label={playing ? 'עצירה' : 'השמעה'}
      title={failed ? 'ההשמעה נכשלה' : undefined}
      disabled={busy}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`cdr-play${size > 40 ? ' lg' : ''}${playing ? ' on' : ''}`}
      style={{
        flex: 'none', width: size, height: size, borderRadius: 99, border: 0, display: 'grid', placeItems: 'center',
        cursor: busy ? 'progress' : 'pointer', background: bg, color: fg, opacity: busy ? 0.6 : 1,
      }}
    >
      <Icon name={playing ? 'pause' : 'play'} size={size > 40 ? 16 : 12} />
    </button>
  );
}

/**
 * השמעת הקלטת שיחה מהרשימה: שולף את קישור ההקלטה החתום (cdrDetail)
 * ומעביר לסרגל הנגן הגלובלי. לחיצה חוזרת על שיחה שמתנגנת סוגרת את הנגן.
 */
export function useCallPlayback() {
  const track = useAudioPlayer((s) => s.track);
  const playGlobal = useAudioPlayer((s) => s.play);
  const closeGlobal = useAudioPlayer((s) => s.close);
  const [srcById, setSrcById] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);

  const playing = useAudioPlayer((s) => s.playing);
  const failed = useAudioPlayer((s) => s.failed);

  // השיחה שטעונה בסרגל; "מנגן" רק כשההשמעה באמת רצה (לא בהשהיה/כישלון).
  const loadedId = track ? Number(Object.keys(srcById).find((id) => srcById[Number(id)] === track.src) ?? NaN) : NaN;
  const playingId = playing && !failed ? loadedId : NaN;

  const toggle = useCallback(async (r: CdrRow) => {
    if (playingId === r.id) { closeGlobal(); return; }
    setBusyId(r.id);
    try {
      const d = await adminApi.cdrDetail(r.id);
      const src = d.recording?.file_url;
      if (!src || d.recording?.has_file === false) { toast('הקלטה לא זמינה', 'danger'); return; }
      setSrcById((m) => ({ ...m, [r.id]: src }));
      const party = remoteParty(r);
      playGlobal({
        src,
        title: party.lookup?.name || formatIsraeli(party.number),
        subtitle: [whenLabel(r.started_at), r.agent_name].filter(Boolean).join(' · ') || undefined,
        duration: d.recording?.duration ?? r.talk_sec,
      });
    } catch {
      toast('שגיאה בטעינת ההקלטה', 'danger');
    } finally {
      setBusyId(null);
    }
  }, [playingId, closeGlobal, playGlobal]);

  return {
    toggle, busyId,
    playingId: Number.isNaN(playingId) ? null : playingId,
    failedId: failed && !Number.isNaN(loadedId) ? loadedId : null,
  };
}

/** שמות קמפיינים ותורים לעמודת "קמפיין / תור" (השיחה מחזירה רק מזהים). */
export function useCampaignQueueNames() {
  const [campaigns, setCampaigns] = useState<Record<number, string>>({});
  const [queues, setQueues] = useState<Record<number, string>>({});
  useEffect(() => {
    const toMap = (res: { data?: unknown }) =>
      Object.fromEntries(((res.data as { id: number; name: string }[]) ?? []).map((x) => [x.id, x.name]));
    // בלי הרשאה למסכים האלה העמודה פשוט נשארת עם מזהה.
    void adminApi.campaigns().then((r) => setCampaigns(toMap(r))).catch(() => {});
    void adminApi.queues().then((r) => setQueues(toMap(r))).catch(() => {});
  }, []);
  return useCallback((r: CdrRow): string | null => {
    if (r.campaign_id) return campaigns[r.campaign_id] ?? `קמפיין #${r.campaign_id}`;
    if (r.queue_id) return `תור: ${queues[r.queue_id] ?? `#${r.queue_id}`}`;
    return null;
  }, [campaigns, queues]);
}
